import React, { useMemo } from 'react';
import { useLogs } from '../../contexts';
import { LogEntry, LogLocation } from '../../types';
import { extractLocationFromText } from '../../utils';

type Problem = {
  filePath: string;
  fileName: string;
  line: number;
  column: number;
  message: string;
  severity: 'error' | 'warning' | 'info';
  source: string;
};

type ProblemsPanelProps = {
  onNavigate: (loc: LogLocation) => void;
};

function getLocationFromLog(log: LogEntry): LogLocation | null {
  const fromMessage = extractLocationFromText(log.message);
  if (fromMessage) return fromMessage;
  if (log.details) {
    for (const line of log.details) {
      const found = extractLocationFromText(line);
      if (found) return found;
    }
  }
  return null;
}

export const ProblemsPanel = React.memo(function ProblemsPanel({ onNavigate }: ProblemsPanelProps) {
  const { logs } = useLogs();

  // Extract problems from logs
  const problems = useMemo<Problem[]>(() => {
    const problemList: Problem[] = [];

    for (const log of logs) {
      const loc = getLocationFromLog(log);
      if (!loc) continue;

      const fileName = loc.path.split('/').pop() || loc.path;

      problemList.push({
        filePath: loc.path,
        fileName,
        line: loc.line,
        column: loc.column,
        message: log.message,
        severity: log.level === 'error' ? 'error' : 'info',
        source: log.task || 'unknown',
      });
    }

    // Sort by severity (errors first) then by file
    return problemList.sort((a, b) => {
      const severityOrder = { error: 0, warning: 1, info: 2 };
      const severityDiff = severityOrder[a.severity] - severityOrder[b.severity];
      if (severityDiff !== 0) return severityDiff;
      return a.filePath.localeCompare(b.filePath);
    });
  }, [logs]);

  // Group problems by file
  const groupedProblems = useMemo(() => {
    const groups: Record<string, Problem[]> = {};
    for (const problem of problems) {
      if (!groups[problem.filePath]) {
        groups[problem.filePath] = [];
      }
      groups[problem.filePath].push(problem);
    }
    return groups;
  }, [problems]);

  // Count by severity
  const counts = useMemo(() => {
    const c = { error: 0, warning: 0, info: 0 };
    for (const p of problems) {
      c[p.severity]++;
    }
    return c;
  }, [problems]);

  const getSeverityIcon = (severity: Problem['severity']) => {
    switch (severity) {
      case 'error':
        return <span className="problem-icon error">×</span>;
      case 'warning':
        return <span className="problem-icon warning">⚠</span>;
      default:
        return <span className="problem-icon info">ℹ</span>;
    }
  };

  return (
    <div className="problems-panel">
      <div className="problems-header">
        <span className="problems-title">Problems</span>
        <div className="problems-counts">
          {counts.error > 0 && (
            <span className="problem-count error">
              <span className="problem-icon error">×</span>
              {counts.error}
            </span>
          )}
          {counts.warning > 0 && (
            <span className="problem-count warning">
              <span className="problem-icon warning">⚠</span>
              {counts.warning}
            </span>
          )}
          {counts.info > 0 && (
            <span className="problem-count info">
              <span className="problem-icon info">ℹ</span>
              {counts.info}
            </span>
          )}
          {problems.length === 0 && (
            <span className="problems-empty">No problems</span>
          )}
        </div>
      </div>

      <div className="problems-list">
        {Object.entries(groupedProblems).map(([filePath, fileProblems]) => (
          <div key={filePath} className="problems-file-group">
            <div className="problems-file-header">
              <span className="problems-file-name">{fileProblems[0].fileName}</span>
              <span className="problems-file-path">{filePath}</span>
              <span className="problems-file-count">{fileProblems.length}</span>
            </div>
            <div className="problems-file-items">
              {fileProblems.map((problem, index) => (
                <button
                  key={`${problem.line}-${problem.column}-${index}`}
                  className={`problems-item ${problem.severity}`}
                  onClick={() => onNavigate({
                    path: problem.filePath,
                    line: problem.line,
                    column: problem.column,
                  })}
                >
                  {getSeverityIcon(problem.severity)}
                  <span className="problems-message">{problem.message}</span>
                  <span className="problems-location">
                    [{problem.line}:{problem.column}]
                  </span>
                  <span className="problems-source">{problem.source}</span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
});
